import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../store/appContext";
import { EMPRESAS, empresaLabel } from "../utils/empresas";
import { confirmar } from "../utils/confirmar";
import { Button, Alert } from "../components/ui";
import GastosFijosPage from "./GastosFijosPage.jsx";

const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const DEFAULT_FORM = {
  fecha: toLocalYMD(new Date()),
  tipo_movimiento: "gasto",
  concepto: "",
  categoria: "general",
  categoria_personalizada: "",
  importe: "",
  proveedor: "",
  observaciones: "",
  empresa: "autospa",
};

const GASTO_CATEGORIAS = [
  {
    grupo: "Personal puntual",
    opciones: [
      ["formacion_prevencion", "Formación y prevención"],
      ["uniformes_epis", "Uniformes y EPIs"],
    ],
  },
  {
    grupo: "Operación",
    opciones: [
      ["productos_materiales", "Productos y materiales"],
      ["proveedores_externos", "Proveedores y trabajos externos"],
      ["reparaciones_mantenimiento", "Reparaciones y mantenimiento"],
      ["combustible_transporte", "Combustible y transporte"],
    ],
  },
  {
    grupo: "Administración",
    opciones: [
      ["publicidad_marketing", "Publicidad y marketing"],
    ],
  },
  {
    grupo: "Fiscal e inversión",
    opciones: [
      ["maquinaria_herramientas", "Maquinaria y herramientas"],
      ["mobiliario_instalaciones", "Mobiliario e instalaciones"],
    ],
  },
  {
    grupo: "Otros",
    opciones: [
      ["gastos_imprevistos", "Imprevistos"],
      ["gastos_extraordinarios", "Gastos extraordinarios"],
      ["otros_gastos", "Otros gastos"],
      ["personalizada", "Gasto personalizado"],
    ],
  },
];

const GASTOS_FRECUENTES = [
  ["productos_materiales", "Productos"],
  ["proveedores_externos", "Proveedores"],
  ["reparaciones_mantenimiento", "Reparaciones"],
  ["combustible_transporte", "Combustible"],
  ["gastos_imprevistos", "Imprevisto"],
  ["personalizada", "Otro gasto"],
];

export default function RegistrarGastoPage() {
  const { actions } = useContext(Context);
  const navigate = useNavigate();

  const darkPanel = {
    background: "var(--sw-surface)",
    border: "1px solid var(--sw-border)",
    color: "var(--sw-text)",
  };
  const darkInputStyle = {
    background: "var(--sw-surface-2)",
    border: "1px solid var(--sw-border)",
    color: "var(--sw-text)",
  };
  const darkInputClass = "form-control";
  const darkSelectClass = "form-select";

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);

  const onChangeForm = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const seleccionarGasto = (categoriaSeleccionada, concepto) => {
    setForm((prev) => ({
      ...prev,
      tipo_movimiento: "gasto",
      categoria: categoriaSeleccionada,
      concepto: categoriaSeleccionada === "personalizada" ? "" : concepto,
      categoria_personalizada: categoriaSeleccionada === "personalizada" ? prev.categoria_personalizada : "",
    }));
    setFormError("");
  };

  const limpiarForm = () => {
    setForm({ ...DEFAULT_FORM, fecha: toLocalYMD(new Date()) });
  };

  const fmtDia = (raw) => {
    const dt = new Date(raw);
    return Number.isNaN(dt.getTime()) ? String(raw || "").slice(0, 10) : toLocalYMD(dt);
  };

  const buscarGastoDuplicado = async (datosForm) => {
    const [year, month] = datosForm.fecha.split("-").map(Number);
    const desde = toLocalYMD(new Date(year, month - 1, 1));
    const hasta = toLocalYMD(new Date(year, month, 0));
    const conceptoNorm = datosForm.concepto.trim().toLowerCase();

    try {
      const resp = await actions.getGastosEmpresa({ desde, hasta, empresa: datosForm.empresa });
      const items = Array.isArray(resp?.items) ? resp.items : [];
      return items.find((g) => {
        const cat = String(g.categoria || "").toLowerCase();
        if (cat === "ingreso_manual" || cat === "ingreso_cobro_inspeccion" || cat === "ingreso_cobro_profesional") return false;
        return String(g.concepto || "").trim().toLowerCase() === conceptoNorm;
      }) || null;
    } catch (err) {
      console.error("buscarGastoDuplicado:", err);
      return null;
    }
  };

  const crearGasto = async (e) => {
    e.preventDefault();

    if (!form.concepto.trim()) { setFormError("El concepto es obligatorio"); return; }
    if (form.tipo_movimiento === "gasto" && form.categoria === "personalizada" && !form.categoria_personalizada.trim()) {
      setFormError("Escribe el nombre de la categoría personalizada");
      return;
    }

    const importe = Number(form.importe);
    if (!Number.isFinite(importe) || importe < 0) { setFormError("El importe debe ser un número válido mayor o igual a 0"); return; }

    setFormError("");
    setSavedMsg("");

    if (form.tipo_movimiento === "gasto") {
      const duplicado = await buscarGastoDuplicado(form);
      if (duplicado) {
        const ok = await confirmar(
          `Ya hay un gasto "${duplicado.concepto}" registrado el ${fmtDia(duplicado.fecha)} `
          + `(${Number(duplicado.importe || 0).toFixed(2)} EUR) para ${empresaLabel(form.empresa)} este mismo mes. `
          + `¿Registrar este de todas formas?`,
          { titulo: "Posible gasto duplicado", labelConfirmar: "Registrar igualmente", labelCancelar: "Cancelar", danger: false }
        );
        if (!ok) return;
      }
    }

    setSaving(true);
    try {
      await actions.createGastoEmpresa({
        fecha: form.fecha,
        concepto: form.concepto,
        categoria: form.tipo_movimiento === "ingreso"
          ? "ingreso_manual"
          : (form.categoria === "personalizada" ? form.categoria_personalizada.trim() : form.categoria),
        importe,
        proveedor: form.proveedor,
        observaciones: form.observaciones,
        empresa: form.empresa,
      });
      limpiarForm();
      setSavedMsg("Movimiento guardado correctamente.");
    } catch (err) {
      setFormError(`No se pudo guardar el gasto: ${err?.message || "error"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4 sw-page-shell sw-view-stack" style={{ color: "var(--sw-text)" }}>
      <div className="d-flex justify-content-between align-items-center mb-3 sw-view-header">
        <h2 className="mb-0" style={{ color: "var(--sw-text)" }}>Registrar gastos</h2>
        <Button variant="outline" onClick={() => navigate("/administracion/finanzas")}>
          Ver Finanzas
        </Button>
      </div>

      <div className="card mb-4" style={darkPanel}>
        <div className="card-header fw-semibold" style={{ background: "var(--sw-surface-light)", color: "var(--sw-text)", borderBottom: "1px solid var(--sw-border)" }}>
          Registrar gasto o movimiento manual
        </div>
        <div className="card-body">
          <div className="mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <span className="fw-semibold" style={{ color: "var(--sw-text)" }}>Gastos frecuentes:</span>
              {GASTOS_FRECUENTES.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  aria-pressed={form.tipo_movimiento === "gasto" && form.categoria === value}
                  onClick={() => seleccionarGasto(value, label)}
                >
                  {label}
                </button>
              ))}
            </div>
            <small style={{ color: "var(--sw-text-muted)" }}>
              Elige un gasto para rellenar el concepto y la categoría. Después indica el importe.
            </small>
          </div>
          <form className="row g-3" onSubmit={crearGasto}>
            <div className="col-md-2">
              <label className="form-label" style={{ color: "var(--sw-text)" }}>Empresa</label>
              <select name="empresa" className={darkSelectClass} style={darkInputStyle} value={form.empresa} onChange={onChangeForm} required>
                {EMPRESAS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ color: "var(--sw-text)" }}>Fecha</label>
              <input type="date" name="fecha" className={darkInputClass} style={darkInputStyle} value={form.fecha} onChange={onChangeForm} required />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ color: "var(--sw-text)" }}>Tipo</label>
              <select
                name="tipo_movimiento"
                className={darkSelectClass}
                style={darkInputStyle}
                value={form.tipo_movimiento}
                onChange={onChangeForm}
              >
                <option value="gasto">Gasto / Pago</option>
                <option value="ingreso">Ingreso manual</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ color: "var(--sw-text)" }}>Concepto</label>
              <input type="text" name="concepto" className={darkInputClass} style={darkInputStyle} value={form.concepto} onChange={onChangeForm} placeholder="Se rellena al elegir el gasto" required />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ color: "var(--sw-text)" }}>Categoría</label>
              <select
                name="categoria"
                className={darkSelectClass}
                style={darkInputStyle}
                value={form.categoria}
                onChange={(e) => {
                  const option = e.target.selectedOptions[0];
                  const nextValue = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    categoria: nextValue,
                    categoria_personalizada: nextValue === "personalizada" ? prev.categoria_personalizada : "",
                    concepto: !prev.concepto || GASTO_CATEGORIAS.some(({ opciones }) => opciones.some(([, label]) => label === prev.concepto))
                      ? (nextValue === "personalizada" ? "" : option.text)
                      : prev.concepto,
                  }));
                }}
                disabled={form.tipo_movimiento === "ingreso"}
              >
                <option value="general">Seleccionar gasto...</option>
                {GASTO_CATEGORIAS.map(({ grupo, opciones }) => (
                  <optgroup key={grupo} label={grupo}>
                    {opciones.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            {form.tipo_movimiento === "gasto" && form.categoria === "personalizada" && (
              <div className="col-md-3">
                <label className="form-label" style={{ color: "var(--sw-text)" }}>Nombre de la categoría</label>
                <input
                  type="text"
                  name="categoria_personalizada"
                  className={darkInputClass}
                  style={darkInputStyle}
                  value={form.categoria_personalizada}
                  onChange={onChangeForm}
                  placeholder="Ej.: Avería urgente"
                  required
                />
              </div>
            )}
            <div className="col-md-2">
              <label className="form-label" style={{ color: "var(--sw-text)" }}>Importe</label>
              <input type="number" min="0" step="0.01" name="importe" className={darkInputClass} style={darkInputStyle} value={form.importe} onChange={onChangeForm} required />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ color: "var(--sw-text)" }}>Proveedor / Destino</label>
              <input type="text" name="proveedor" className={darkInputClass} style={darkInputStyle} value={form.proveedor} onChange={onChangeForm} />
            </div>
            <div className="col-12">
              <label className="form-label" style={{ color: "var(--sw-text)" }}>Observaciones</label>
              <textarea name="observaciones" className={darkInputClass} style={darkInputStyle} rows="2" value={form.observaciones} onChange={onChangeForm} />
            </div>
            {formError && (
              <div className="col-12">
                <Alert variant="danger" onClose={() => setFormError("")}>
                  {formError}
                </Alert>
              </div>
            )}
            {savedMsg && (
              <div className="col-12">
                <Alert variant="success" onClose={() => setSavedMsg("")}>
                  {savedMsg}
                </Alert>
              </div>
            )}
            <div className="col-12 d-flex gap-2">
              <Button type="submit" variant="primary" loading={saving}>
                Guardar gasto
              </Button>
              <Button type="button" variant="outline" onClick={limpiarForm}>
                Limpiar
              </Button>
            </div>
          </form>
        </div>
      </div>

      <GastosFijosPage embedded />
    </div>
  );
}
